/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/

import { VisionApplication } from '../application';
import { DeviceDTO, DeviceService } from '../service/device';
import { VisionWindowManager } from './manager';
import { VisionWindow } from './window';

export class VisionDeviceWindow extends VisionWindow {
    private layout!: dhtmlXLayoutObject;
    private devices!: dhtmlXGridObject;
    private form!: dhtmlXForm;
    private toolbar!: dhtmlXToolbarObject;
    private deviceCache!: Map<string, DeviceDTO>;
    private deviceService!: DeviceService;

    constructor(manager: VisionWindowManager) {
        super(manager, {
            id: 'edit_device_window',
            title: 'Devices',
            x: 50,
            y: 50,
            width: 800,
            height: 600,
        });
        this.window.setText('Devices');
        this.modal = true;

        this.deviceService = this.manager.app.deviceService;
        this.deviceCache = new Map<string, DeviceDTO>();

        this.buildLayout();
        this.setupEvents();
        this.buildDeviceCache();
    }

    /**
     *
     */
    public show() {
        super.show();
        this.clearForm();
        this.buildDeviceCache();
    }

    /**
     *
     */
    private clearForm(clearSelection = true) {
        this.form.setItemValue('id', null);
        this.form.setItemValue('name', null);
        this.form.setItemValue('description', null);
        this.form.setItemValue('device_type', 'NONE');
        this.form.setItemValue('network_id_1', null);
        this.form.setItemValue('network_id_2', null);
        this.form.reloadOptions('assigned_asset', []);

        const assigned = this.form.getSelect('assigned_asset');
        assigned.add(new Option('Unassigned', 'unassigned'));
        const assetList = VisionApplication.getInstance().assetService.assetList;
        assetList.forEach((asset) => {
            if (asset.device.type == 'NONE') {
                assigned.add(new Option(asset.id.name, asset.id.id));
            }
        });

        if (clearSelection) {
            this.devices.clearSelection();
        }
    }

    /** Builds the window layout
     *
     */
    private buildLayout() {
        this.layout = this.window.attachLayout('2U');
        this.layout.cells('a').hideArrow();
        this.layout.cells('b').hideArrow();

        // Device Grid
        this.layout.cells('a').setText('Devices');
        this.devices = this.layout.cells('a').attachGrid();
        this.devices.setHeader('Name,IMEI 1, IMEI 2,Type,Assigned', '', []);
        this.devices.setColSorting('str,str,str,str,str');
        this.devices.init();
        this.devices.enableHeaderMenu();
        this.devices.setColumnHidden(2, true);

        // Device Properties
        this.layout.cells('b').setText('Properties');
        this.toolbar = this.layout.cells('b').attachToolbar();
        this.toolbar.addButton('new', 0, 'New', 'icons/sign.png', '');
        this.toolbar.addButton('save', 1, 'Save', 'css/imgs/save.gif', '');
        this.toolbar.addSeparator('sep1', 2);
        this.toolbar.addButton('delete', 3, 'Delete', 'icons/clear.png', '');
        this.toolbar.attachEvent('onClick', (itemId) => {
            this.toolbarEvent(itemId);
        });

        // Form
        const form_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 90,
                inputWidth: 130,
            },
            {
                name: 'id',
                type: 'input',
                label: 'ID',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'name',
                type: 'input',
                label: 'Name',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                required: false,
            },
            {
                name: 'description',
                type: 'input',
                label: 'Description',
                value: '',
                rows: '3',
                inputWidth: '250',
                offsetLeft: '25',
            },
            {
                name: 'device_type',
                type: 'select',
                label: 'Type',
                value: '',
                offsetLeft: '25',
                required: true,
                options: [
                    { value: 'NONE', text: 'NONE' },
                    { value: 'CYPRESS_CTMONE', text: 'CTM-ONE' },
                    { value: 'FMC640', text: 'FMC640' },
                    { value: 'FMC00A', text: 'FMC00A' },
                    { value: 'FMM00A', text: 'FMM00A' },
                    { value: 'FMC650', text: 'FMC650' },
                    { value: 'FMM230', text: 'FMM230' },
                ],
            },
            {
                name: 'assigned_asset',
                type: 'select',
                label: 'Asset',
                value: '',
                offsetLeft: '25',
                required: false,
                options: [],
            },
            {
                name: 'network_id_1',
                type: 'input',
                label: 'Primary IMEI:',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                required: true,
            },
            {
                name: 'network_id_2',
                type: 'input',
                label: 'Secondary IMEI:',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
            },
        ];
        this.form = this.layout.cells('b').attachForm({});
        this.form.loadStruct(form_data);

        const assigned = this.form.getSelect('assigned_asset');
        assigned.add(new Option('Unassigned', 'unassigned'));

        const assetList = VisionApplication.getInstance().assetService.assetList;
        assetList.forEach((asset) => {
            if (asset.device.type == 'NONE') {
                assigned.add(new Option(asset.id.name, asset.id.id));
            }
        });

        this.layout.cells('a').hideHeader();
        this.layout.cells('b').hideHeader();
    }

    private rebuildAssetList() {
        const assigned = this.form.getSelect('assigned_asset');
        assigned.add(new Option('Unassigned', 'unassigned'));

        const assetList = VisionApplication.getInstance().assetService.assetList;
        assetList.forEach((asset) => {
            if (asset.device.type == 'NONE') {
                assigned.add(new Option(asset.id.name, asset.id.id));
            }
            if (this.form.getItemValue('network_id_1') == asset.device.networkId1) {
                assigned.add(new Option(asset.id.name, asset.id.id, true, true));
            }
        });
    }

    /** Set up the event handlers
     *
     */
    private setupEvents() {
        this.devices.attachEvent('onRowSelect', (id, _index) => {
            this.selectDevice(id);

            this.form.reloadOptions('assigned_asset', []);
            const assigned = this.form.getSelect('assigned_asset');

            const assetList = VisionApplication.getInstance().assetService.assetList;
            assigned.add(new Option('Unassigned', 'unassigned'));

            assetList.forEach((asset) => {
                if (asset.device.type == 'NONE') {
                    assigned.add(new Option(asset.id.name, asset.id.id));
                }
                if (this.form.getItemValue('network_id_1') == asset.device.networkId1) {
                    assigned.add(new Option(asset.id.name, asset.id.id, true, true));
                }
            });
        });
    }

    /** Build up the list of devices from the server. */
    private buildDeviceCache() {
        this.deviceCache.clear();
        const devices = this.deviceService.getDevices();
        devices.then((data) => {
            if (data.parsedBody) {
                this.deviceCache.clear();
                for (let i = 0; i < data.parsedBody?.length; i++) {
                    this.deviceCache.set(data.parsedBody[i].id, data.parsedBody[i]);
                }
                this.deviceTableFromCache();
            }
        });
    }

    private deviceTableFromCache() {
        this.devices.clearAll(false);
        this.deviceCache.forEach((entry) => {
            this.addDeviceToGrid(entry);
        });
        this.devices.adjustColumnSize(1);
    }

    private addDeviceToGrid(deviceData: DeviceDTO) {
        const rowData = [];
        rowData.push(deviceData.name);
        rowData.push(deviceData.networkId1);
        rowData.push(deviceData.networkId2);
        rowData.push(deviceData.type);

        if (deviceData.assigned != null) {
            const asset = VisionApplication.getInstance().assetService.assetList.get(deviceData.assigned);
            if (asset) {
                rowData.push(asset.id.name);
            }
        } else {
            rowData.push('-');
        }

        console.log(rowData.join(','));
        this.devices.addRow(deviceData.id, rowData.join(','), 0, '', '', false);
        this.devices.setUserData(deviceData.id, 'description', deviceData.description);
    }

    /** Select a device and populate it's data into the form.
     *  id: String => The id of the device
     */
    private selectDevice(id: string) {
        this.clearForm(false);
        const device = this.deviceCache.get(id);
        this.form.setItemValue('id', device?.id);
        this.form.setItemValue('name', device?.name);
        this.form.setItemValue('description', device?.description);
        this.form.setItemValue('device_type', device?.type);
        this.form.setItemValue('network_id_1', device?.networkId1);
    }

    private toolbarEvent(itemId: string) {
        switch (itemId) {
            case 'new': {
                this.clearForm();
                break;
            }
            case 'save': {
                this.saveDevice();
                break;
            }
            case 'delete': {
                this.deleteDevice();
                break;
            }
        }
    }

    /** Delete a device from the server.
     *
     *
     */
    private deleteDevice(): void {
        const id = this.form.getItemValue('id');
        if (id.length <= 0) {
            dhtmlx.message({
                title: 'No Device',
                text: 'No Device was selected',
                type: 'alert-warning',
            });
            return;
        }

        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Delete Device',
            ok: 'Delete Device',
            cancel: 'Cancel',
            text: 'This will delete the device and remove any link to it assigned asset.',
            callback: (result: boolean) => {
                if (result == false) {
                    return;
                } else {
                    this.sendDeleteToServer();
                }
            },
        });
    }

    private sendDeleteToServer(): void {
        const id = this.form.getItemValue('id');
        this.deviceService.deleteDevice(id).then((_data) => {
            dhtmlx.message({
                title: 'Deleted',
                text: 'Device was removed',
                type: 'alert-success',
            });
            this.clearForm();
            this.buildDeviceCache();
            return;
        });
    }

    private validate(): boolean {
        if (this.form.getItemValue('device_type') == 'NONE') {
            dhtmlx.message({
                title: 'Missing Type',
                text: 'Please select type of device.',
                type: 'alert-error',
            });
            return false;
        }

        if (this.form.getItemValue('network_id_1') == '') {
            dhtmlx.message({
                title: 'Missing IMEI',
                text: 'Please provide IMEI.',
                type: 'alert-error',
            });
            return false;
        }

        return true;
    }

    /** Save the device to the server.
     * If there is no id ( it's a new device ) then will ask if want to create an asset as well.
     */
    private saveDevice() {
        if (this.validate() == false) {
            return;
        }

        const id = this.form.getItemValue('id');
        const assigned_device = this.form.getItemValue('assigned_asset');

        if (id.length <= 0 && assigned_device == 'unassigned') {
            dhtmlx.confirm({
                type: 'confirm',
                title: 'Create Asset',
                ok: 'Yes',
                cancel: 'No',
                text: 'Create an asset with this new device?',
                callback: (result: boolean) => {
                    this.saveDeviceToServer(result);
                },
            });
        } else {
            this.saveDeviceToServer(false);
        }
    }

    private saveDeviceToServer(createAsset: boolean) {
        const save = {} as DeviceDTO;

        save.id = this.form.getItemValue('id');
        save.name = this.form.getItemValue('name');
        save.description = this.form.getItemValue('description');
        save.type = this.form.getItemValue('device_type');
        save.networkId1 = this.form.getItemValue('network_id_1');
        save.networkId2 = this.form.getItemValue('network_id_2');
        save.assigned = this.form.getItemValue('assigned_asset');

        let request;
        console.log(save.id.length);
        if (save.id.length > 0) {
            request = this.deviceService.updateDevice(save);
        } else {
            request = this.deviceService.saveDevice(save, createAsset);
        }

        request.then((data) => {
            if (data.parsedBody) {
                const id = data.parsedBody.id;

                dhtmlx.message({
                    title: 'Save Complete',
                    text: 'Device ' + data.parsedBody?.name + ' was saved.',
                    type: 'alert-success',
                });

                VisionApplication.getInstance()
                    .assetService.cacheAssets()
                    .then(() => {
                        this.clearForm();
                        this.rebuildAssetList();
                        this.selectDevice(id);
                    });
            }

            this.buildDeviceCache();
        });
    }
}
